/**
 * TaxDueWidget
 * Fordon med fordonsskatt senast inom 45 dagar (sista dagen i betalningsmånad).
 */

import React, { useMemo, useState } from 'react';
import { useTranslation, useLanguage } from '@/store/languageStore';
import { uiDateLocale } from '@/utils/uiLocale';
import { BanknotesIcon, ExclamationTriangleIcon } from '@heroicons/react/24/outline';
import { BaseDashboardWidget } from './BaseDashboardWidget';
import VehicleRegistrationLink from './VehicleRegistrationLink';

interface TaxVehicle {
  vehicleId: number;
  registrationNumber: string;
  make?: string;
  model?: string;
  organizationName?: string;
  taxDueDate: string;
  daysUntil: number;
  isOverdue: boolean;
}

interface TaxMeta {
  totalCount: number;
  overdueCount: number;
  daysAhead: number;
}

const TaxDueWidget: React.FC<{ startExpanded?: boolean; onRemove?: () => void }> = ({ startExpanded = false }) => {
  const { t } = useTranslation();
  const { language } = useLanguage();
  const [meta, setMeta] = useState<TaxMeta | null>(null);

  const vehicleAssignments = useMemo(
    () => ({
      categoryCode: 'TAX',
      getVehicleId: (v: TaxVehicle) => v.vehicleId,
      getRegistrationNumber: (v: TaxVehicle) => v.registrationNumber,
      buildAssignmentTitle: (v: TaxVehicle) =>
        `${t('taxWidget.titleWithDays')}: ${v.registrationNumber}`,
    }),
    [t],
  );

  const formatDate = (dateStr: string) => {
    const [y, m, d] = dateStr.split('-').map(Number);
    const date = new Date(y, (m || 1) - 1, d || 1);
    return new Intl.DateTimeFormat(uiDateLocale(language), {
      day: 'numeric',
      month: 'short',
    }).format(date);
  };

  const getDaysLabel = (days: number) => {
    if (days < 0) {
      return t('taxWidget.daysAgo').replace('{days}', String(Math.abs(days)));
    }
    if (days === 0) return t('taxWidget.today');
    return `${days}d`;
  };

  return (
    <BaseDashboardWidget<TaxVehicle>
      icon={BanknotesIcon}
      colorScheme="teal"
      title={t('taxWidget.titleWithDays')}
      subtitle={t('taxWidget.titleWithDays')}
      apiEndpoint="/api/dashboard/data/upcoming-tax-payments"
      apiParams={{ days: 45 }}
      emptyMessage={t('taxWidget.noVehicles')}
      getItemKey={(vehicle) => vehicle.vehicleId}
      initialExpanded={startExpanded}
      listClassName={startExpanded ? 'max-h-[60vh] overflow-y-auto space-y-1' : undefined}
      vehicleAssignments={vehicleAssignments}
      onDataLoaded={(_, metaData) => setMeta(metaData)}
      renderBadge={() => {
        const overdueCount = meta?.overdueCount || 0;
        if (overdueCount === 0) return null;
        return (
          <span className="flex items-center gap-1 bg-red-100 dark:bg-red-900/30 text-red-600 dark:text-red-400 text-xs font-medium px-2 py-1 rounded-full">
            <ExclamationTriangleIcon className="w-3 h-3" />
            {overdueCount} {t('taxWidget.overdue')}
          </span>
        );
      }}
      renderItem={(vehicle) => (
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <VehicleRegistrationLink
              registrationNumber={vehicle.registrationNumber}
              vehicleId={vehicle.vehicleId}
              className="font-medium"
            />
            {vehicle.make && (
              <span className="text-xs text-textTertiary">{vehicle.make}</span>
            )}
          </div>
          <div className="text-xs">
            <span
              className={
                vehicle.isOverdue
                  ? 'text-red-500'
                  : vehicle.daysUntil <= 14
                    ? 'text-orange-500'
                    : 'text-textTertiary'
              }
            >
              {formatDate(vehicle.taxDueDate)} ({getDaysLabel(vehicle.daysUntil)})
            </span>
          </div>
        </div>
      )}
    />
  );
};

export default TaxDueWidget;
